import DB from './index.js';
import { queryPackageInfoByscanCode } from '@/api/biz/shipment/shipmentService';
import { addLog } from '@/api/biz/master/masterService';
import { userInfo } from '@/global';
import * as FeAddlog from "../../api/lib/addLog.js";

const orderFaceDbInstance = new DB('iWMS_DB_order_face');

// 运行池
const pool = new Set();

// 等待队列
const waitQueue = [];

// 最大并发量
const MAX_PARALLEL_NUM = 6;

// 最大缓存量
const MAX_STORAGE_NUM = 20;

// 已缓存的面单数据key
let storagedKeys = [];

// 初始化时, 获取已缓存的数据, 并删除超出最大缓存量的数据
orderFaceDbInstance.keys().then(keys => {
    // 如果缓存数量超过最大缓存量，则删除超出部分
    if (keys.length > MAX_STORAGE_NUM) {
        const keysToDelete = keys.splice(0, keys.length - MAX_STORAGE_NUM);
        keysToDelete.forEach(key => {
            orderFaceDbInstance.removeItem(key);
            costTimeMap.delete(key);
        });
    }
    storagedKeys = keys;
});

/**
 * @description 检查缓存数量是否超过最大缓存量，如果超过则删除最早缓存的数据(队头)
 * @param {String} orderId 订单id
 */
const checkStorage = (orderId, skipCheck = false) => {
    storagedKeys.push(orderId);
    if (!skipCheck && storagedKeys.length > MAX_STORAGE_NUM) {
        const keyToDelete = storagedKeys.shift();
        orderFaceDbInstance.removeItem(keyToDelete);
        costTimeMap.delete(keyToDelete)
    }
}

/**
 * @description 简版LRU, 读取数据后, 更新缓存数据的位置到队尾
 * @param {String} orderId 订单id
 */
const updatePosition = (orderId) => {
    const index = storagedKeys.indexOf(orderId);
    if (index > -1) {
        storagedKeys.splice(index, 1);
    }
    checkStorage(orderId)
}

// TODO: 线上运行平稳后删除记录耗时&日志逻辑
// 记录每个订单读取缓存和远程请求的耗时
const costTimeMap = new Map();
const initMapItem = (id) => {
    if (!costTimeMap.get(id)) {
        costTimeMap.set(id, {
            storage: 0, // 读取缓存耗时
            remote: 0,  // 远程请求耗时
            upRate: 0, // 缓存相比远程提速比例
            noStorage: false,  // 读取缓存时是否读取不到
            faceNum: 0 // 面单数量
        })
    }
}

// 获取远程数据, 并记录接口耗时
const getRemoteData = async (orderId, skipCheck = false) => {
    const remoteStart = +new Date();
    let timeStr = new Date().getTime();
    FeAddlog.addLog('storageOrderFaceData', '远程获取订单面单数据请求', {
        scanCode: orderId,
        timeStr: timeStr
    })
    const data = await queryPackageInfoByscanCode({ scanCode: orderId });
    try {
        FeAddlog.addLog('storageOrderFaceData', '远程获取订单面单数据返回', {
            outBoundNo: orderId,
            timeStr: timeStr,
            data: JSON.stringify(data).slice(0, 1000)
        })
    } catch (e) {}
    if (data && data !== 1801375) {
        orderFaceDbInstance.setItem(orderId, data);
        checkStorage(orderId, skipCheck);
    }
    if (costTimeMap.get(orderId)) {
      costTimeMap.get(orderId).remote = +new Date() - remoteStart;
      costTimeMap.get(orderId).faceNum = Array.isArray(data) ? data.length : 0;
    }
    return data;
}

/**
 * @description 限制并发数量的请求
 * @param {Function} reqFn 请求方法
 * @param {Number} max 最大并发量
 */
const parallelRequest = (reqFn, max = MAX_PARALLEL_NUM) => {
    return new Promise((resolve, reject) => {
        if (typeof reqFn !== 'function') {
            return reject(new Error('reqFn must be a function'));
        }
        // 判断运行池是否已满
        const isFull = pool.size >= max;
        // 包装的新请求
        const newReqFn = () => {
            reqFn()
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    reject(err);
                })
                .finally(() => {
                    // 请求完成后，将该请求从运行池中删除
                    pool.delete(newReqFn);
                    // 从等待队列中取出一个新请求放入等待运行池执行
                    const next = waitQueue.shift();
                    if (next) {
                        pool.add(next);
                        next();
                    }
                });
        };
        if (isFull) {
            // 如果运行池已满，则将新的请求放到等待队列中
            waitQueue.push(newReqFn);
        } else {
            // 如果运行池未满，则向运行池中添加一个新请求并执行该请求
            pool.add(newReqFn);
            newReqFn();
        }
    })
}

const logParams = {
    bizType: 'storageOrderFaceData',
    bizName: 'storageOrderFaceData',
    createUser: userInfo.userCode,
    cost: 0
}

/**
 * @description 远程获取订单列表对应的面单数据并缓存
 * @param {Array} orderIdList 订单列表
 */
export const setOrderFaceList = async (orderIdList) => {
    addLog({
        ...logParams,
        bizKey: `storageOrderFaceData_setOrderFaceList`,
        content: JSON.stringify(orderIdList)
    })
    if (!Array.isArray(orderIdList)) {
        console.error('orderIdList must be an array');
        return;
    }
    const validOrderIdList = orderIdList.filter(Boolean);
    const needClear = validOrderIdList.length >= MAX_STORAGE_NUM;
    // 如果订单列表长度超出缓存数量上限，则清空缓存
    if (needClear) {
        await orderFaceDbInstance.clear();
        storagedKeys = [];
    }
    validOrderIdList.forEach(async orderId => {
        if (!orderId) return;
        initMapItem(orderId);
        const res = await orderFaceDbInstance.getItem(orderId);
        if (res) {
            updatePosition(orderId);
            return;
        };
        parallelRequest(() => getRemoteData(orderId, needClear))
    });
};

/**
 * @description 远程获取订单对应的面单数据并缓存
 * @param {String} orderId 订单id
 */
export const setOrderFace = async (orderId) => {
    addLog({
        ...logParams,
        bizKey: `storageOrderFaceData_setOrderFace`,
        content: JSON.stringify({ orderId, url: window.location.href })
    })
    if (!orderId) {
        console.error('orderId is required');
        return;
    }
    initMapItem(orderId);
    const res = await orderFaceDbInstance.getItem(orderId);
    try {
        FeAddlog.addLog('storageOrderFaceData', 'setOrderFace', {
            orderId: orderId,
            res: JSON.stringify(res).slice(0, 1000)
        })
    } catch (e) {}
    if (res) {
        updatePosition(orderId);
        return;
    };
    getRemoteData(orderId);
};

/**
 * @description 远程本地缓存的面单数据, 如果本地没有则远程获取
 * @param {String} orderId 订单id
 * @returns {Object} 面单数据
 */
export const getOrderFace = async (orderId) => {
    addLog({
        ...logParams,
        bizKey: `storageOrderFaceData_getOrderFace`,
        content: JSON.stringify({ orderId, url: window.location.href })
    })
    if (!orderId) {
        console.error('orderId is required');
        return;
    }
    initMapItem(orderId);
    const storageStart = +new Date();
    let res = await orderFaceDbInstance.getItem(orderId);
    const costItem = costTimeMap.get(orderId);
    costItem.noStorage = !res;
    try {
        FeAddlog.addLog('storageOrderFaceData', 'getOrderFace', {
            orderId: orderId,
            res: JSON.stringify(res).slice(0, 1000)
        });
    } catch (e) {}
    if (!res) {
        console.error('该订单数据未缓存');
        const data = await getRemoteData(orderId);
        res = data
    } else {
        updatePosition(orderId);
    }
    costItem.storage = +new Date() - storageStart;
    costItem.upRate = (costItem.remote - costItem.storage) / costItem.remote * 100;
    addLog({
        ...logParams,
        bizKey: `storageOrderFaceData_getOrderFace_cost`,
        content: JSON.stringify({ orderId, cost: costItem, url: window.location.href })
    })
    return res;
};
